import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  Building2, 
  Search, 
  Filter,
  Globe,
  DollarSign,
  Plane,
  Plus,
  ChevronRight
} from 'lucide-react';
import { API, useAuth } from '@/App';

const Jobs = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const headerRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [workArrangements, setWorkArrangements] = useState([]);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [jobType, setJobType] = useState('');
  const [workArrangement, setWorkArrangement] = useState('');
  const [visaSponsorship, setVisaSponsorship] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Cache list state across route navigation so back from a job detail
  // lands the user instantly on the same list at the page header.
  useListStatePersistence({
    key: 'jobs',
    state: { jobs, total, search, category, jobType, workArrangement, visaSponsorship },
    enabled: !loading && jobs.length > 0,
    onRestore: (cached) => {
      if (Array.isArray(cached.jobs)) setJobs(cached.jobs);
      if (typeof cached.total === 'number') setTotal(cached.total);
      if (typeof cached.search === 'string') setSearch(cached.search);
      if (typeof cached.category === 'string') setCategory(cached.category);
      if (typeof cached.jobType === 'string') setJobType(cached.jobType);
      if (typeof cached.workArrangement === 'string') setWorkArrangement(cached.workArrangement);
      if (typeof cached.visaSponsorship === 'string') setVisaSponsorship(cached.visaSponsorship);
      setLoading(false);
      requestAnimationFrame(() => {
        headerRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
    },
  });

  useEffect(() => {
    fetchCategories();
    fetchJobs();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchJobs();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, category, jobType, workArrangement, visaSponsorship]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/jobs/categories`);
      setCategories(response.data.categories);
      setJobTypes(response.data.job_types);
      setWorkArrangements(response.data.work_arrangements);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (jobType) params.append('job_type', jobType);
      if (workArrangement) params.append('work_arrangement', workArrangement);
      if (visaSponsorship === 'yes') params.append('visa_sponsorship', 'true');
      
      const response = await axios.get(`${API}/jobs?${params.toString()}`);
      setJobs(response.data.jobs);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setCategory('');
    setJobType('');
    setWorkArrangement('');
    setVisaSponsorship('');
  };

  const formatSalary = (job) => {
    if (!job.salary_min && !job.salary_max) return null;
    const currency = job.salary_currency || 'USD';
    const period = job.salary_period || 'year';
    
    let salary = '';
    if (job.salary_min && job.salary_max) {
      salary = `${currency} ${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()}`;
    } else if (job.salary_min) {
      salary = `From ${currency} ${job.salary_min.toLocaleString()}`;
    } else {
      salary = `Up to ${currency} ${job.salary_max.toLocaleString()}`;
    }
    
    return `${salary}/${period}`;
  };

  const getWorkArrangementColor = (arrangement) => {
    switch (arrangement) {
      case 'remote': return 'bg-green-100 text-green-700';
      case 'hybrid': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-full mb-6 shadow-lg">
              <Briefcase className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4" ref={headerRef}>
              Expat Job Board
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Find international opportunities with visa sponsorship and remote-friendly positions. 
              Your next career move starts here.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search jobs, companies..."
                  className="h-14 pl-12 pr-4 rounded-full border-2 text-lg"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="h-14 px-6 rounded-full border-2"
              >
                <Filter className="w-5 h-5 mr-2" />
                Filters
              </Button>
              {user && (
                <Link to="/jobs/create">
                  <Button className="h-14 px-6 rounded-full bg-primary">
                    <Plus className="w-5 h-5 mr-2" />
                    Post Job
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>

          {/* Filters */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-2xl p-6 border-2 border-border mb-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={jobType || "all"} onValueChange={(v) => setJobType(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Job Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {jobTypes.map((type) => (
                      <SelectItem key={type} value={type} className="capitalize">{type.replace('-', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={workArrangement || "all"} onValueChange={(v) => setWorkArrangement(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Work Arrangement" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Arrangements</SelectItem>
                    {workArrangements.map((arr) => (
                      <SelectItem key={arr} value={arr} className="capitalize">{arr}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={visaSponsorship || "any"} onValueChange={(v) => setVisaSponsorship(v === "any" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Visa Sponsorship" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="yes">Visa Sponsorship Available</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end mt-4">
                <Button variant="ghost" onClick={clearFilters}>Clear All Filters</Button>
              </div>
            </motion.div>
          )}

          {/* Results Count */}
          <div className="flex justify-between items-center mb-6">
            <p className="text-muted-foreground">
              {total} {total === 1 ? 'job' : 'jobs'} found
            </p>
          </div>

          {/* Job Listings */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 border-2 border-border text-center">
              <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">No jobs found</h3>
              <p className="text-muted-foreground mb-6">
                Try adjusting your filters or check back later for new opportunities.
              </p>
              {user && (
                <Link to="/jobs/create">
                  <Button className="rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Post the First Job
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job, index) => (
                <motion.div
                  key={job.job_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link to={`/jobs/${job.job_id}`}>
                    <div className="bg-white rounded-2xl p-6 border-2 border-border hover:border-primary/50 transition-all group">
                      <div className="flex items-start gap-4">
                        {/* Company Logo */}
                        <div className="w-14 h-14 bg-muted rounded-xl flex items-center justify-center flex-shrink-0">
                          {job.company_logo ? (
                            <img src={job.company_logo} alt={job.company_name} className="w-full h-full object-cover rounded-xl" />
                          ) : (
                            <Building2 className="w-7 h-7 text-muted-foreground" />
                          )}
                        </div>

                        {/* Job Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className="text-lg font-bold group-hover:text-primary transition-colors">
                                {job.title}
                              </h3>
                              <p className="text-muted-foreground">{job.company_name}</p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
                          </div>

                          <div className="flex flex-wrap gap-2 mt-3">
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-muted">
                              <MapPin className="w-3.5 h-3.5" />
                              {job.city}, {job.country}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm capitalize ${getWorkArrangementColor(job.work_arrangement)}`}>
                              <Globe className="w-3.5 h-3.5" />
                              {job.work_arrangement}
                            </span>
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-muted capitalize">
                              <Clock className="w-3.5 h-3.5" />
                              {job.job_type.replace('-', ' ')}
                            </span>
                            {job.visa_sponsorship && (
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-accent/10 text-accent font-medium">
                                <Plane className="w-3.5 h-3.5" />
                                Visa Sponsorship
                              </span>
                            )}
                          </div>

                          {formatSalary(job) && (
                            <p className="mt-3 text-sm font-medium text-green-600 flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              {formatSalary(job)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {/* CTA for non-logged-in users */}
          {!user && (
            <div className="mt-12 bg-gradient-to-r from-primary to-accent rounded-3xl p-8 text-center text-white">
              <h2 className="text-2xl font-bold mb-4">Want to Post a Job?</h2>
              <p className="text-white/90 mb-6 max-w-lg mx-auto">
                Sign in to post job listings and reach our global community of talented expats.
              </p>
              <Link to="/">
                <Button className="bg-white text-primary hover:bg-white/90 rounded-full font-bold px-8">
                  Sign In
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Jobs;
